import { useAuth0 } from '@auth0/auth0-react';
import { LogoutWarningDialog } from '@strata/react-dialog';
import { BlankScreen } from '@strata/react-page-templates';
import dayjs from 'dayjs';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Outlet, useLocation } from 'react-router';

import { Header } from 'components/header/Header';
import { clearDataForLogout, getFMHTokenAndEnsureSocketConnections } from 'services/authHelpers';
import { LOGIN_REDIRECT_KEY, TOKEN_FETCHED_AT } from 'services/constants';
import { identifyLaunchDarklyUser } from 'services/launchDarkly';
import { syncPendoVisitor } from 'services/pendoUtils';
import { AppRoutes } from 'services/routes';
import { setCanLogWebsocketPerformance } from 'store/userSupport/userSupportSlice';

const TOKEN_REFRESH_THRESHOLD = 5; // minutes
const LOGOUT_THRESHOLD = 59; // minutes

/** Routes that header is hidden on */
const NoHeaderRoutes = [AppRoutes.termsOfUse, AppRoutes.privacyPolicy, AppRoutes.updatesHistory];

/**
 * Component to determine if a user is authenticated and
 * if they are allowed to visit the page they navigated to.
 *
 * If they are: they proceed to the page
 * If not: they are redirected to the login page.
 *
 * This component will also refresh the access token if the user is authenticated.
 */
export const RequireAuth = () => {
    const { isAuthenticated, loginWithRedirect, getAccessTokenSilently, logout, user } = useAuth0();
    const location = useLocation();
    const dispatch = useDispatch();
    const ldClient = useLDClient();
    const {
        userLogoutEnhancement,
        canLogWebsocketPerformance,
        showWorkflowManagementLayer = false,
    } = useFlags();
    const [pendoRole, setPendoRole] = useState();
    // AuthCallback identifies only on login. A refresh must identify before workflow_pilot is recorded.
    const [identifiedEmail, setIdentifiedEmail] = useState();
    const [retrievingTokens, setRetrievingTokens] = useState(false);

    const getAccessTokens = useCallback(
        async ({ activityRefresh = false }) => {
            if (
                !activityRefresh ||
                (isAuthenticated &&
                    !retrievingTokens &&
                    !!user &&
                    dayjs().diff(localStorage.getItem(TOKEN_FETCHED_AT), 'minutes') >=
                        TOKEN_REFRESH_THRESHOLD)
            ) {
                setRetrievingTokens(true);
                await getAccessTokenSilently({
                    cacheMode: 'off', // Prevents retrieving from cache
                });
                const fmhToken = await getFMHTokenAndEnsureSocketConnections({
                    getAccessTokenSilently,
                    logout,
                    dispatch,
                });
                if (fmhToken?.role_name) {
                    setPendoRole(fmhToken.role_name);
                }
                localStorage.setItem(TOKEN_FETCHED_AT, dayjs().toISOString());
                setRetrievingTokens(false);
            }
        },
        [dispatch, getAccessTokenSilently, isAuthenticated, logout, retrievingTokens, user],
    );

    const wrapGetAccessTokenSilently = useCallback(async () => {
        try {
            // Call Auth0 API to get access token, which will refresh the session and cache the token
            await getAccessTokens({});
        } catch (e) {
            // An error in getting the access token invalidates the session and will require logging in again.
            // eslint-disable-next-line no-console
            console.error('Error getting access token silently', e);
        }
    }, [getAccessTokens]);

    useEffect(() => {
        // if feature flag for logout warning dialog is not on, still fetch refresh token on navigation
        if (!userLogoutEnhancement) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            wrapGetAccessTokenSilently();
        }
        if (!retrievingTokens && !isAuthenticated) {
            sessionStorage.setItem(
                LOGIN_REDIRECT_KEY,
                `${location.pathname}${location.search}${location.hash}`,
            );
            loginWithRedirect();
            // AuthCallback will handle the redirect
        }
    }, [
        isAuthenticated,
        location,
        loginWithRedirect,
        retrievingTokens,
        userLogoutEnhancement,
        wrapGetAccessTokenSilently,
    ]);

    useEffect(() => {
        dispatch(setCanLogWebsocketPerformance(canLogWebsocketPerformance));
    }, [dispatch, canLogWebsocketPerformance]);

    useEffect(() => {
        if (!isAuthenticated || !user?.email || !ldClient) {
            return undefined;
        }
        let cancelled = false;
        const email = user.email.toLowerCase();
        void (async () => {
            if (ldClient.getContext?.()?.key !== email) {
                await identifyLaunchDarklyUser(ldClient, user);
            }
            if (!cancelled) {
                setIdentifiedEmail(email);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, user, ldClient]);

    useEffect(() => {
        if (!isAuthenticated || !user || identifiedEmail !== user.email?.toLowerCase()) {
            return;
        }
        syncPendoVisitor({
            user,
            role: pendoRole,
            workflowPilot: showWorkflowManagementLayer === true,
        });
    }, [isAuthenticated, user, pendoRole, showWorkflowManagementLayer, identifiedEmail]);

    if (!isAuthenticated) {
        return <BlankScreen />;
    }

    return (
        <>
            {!NoHeaderRoutes.includes(location.pathname) && <Header />}
            {userLogoutEnhancement && (
                <LogoutWarningDialog
                    tokenFetchedAt={localStorage.getItem(TOKEN_FETCHED_AT)}
                    onExtend={getAccessTokens}
                    onLogout={async (isUserInitiated) => {
                        clearDataForLogout(isUserInitiated);
                        await logout({
                            logoutParams: {
                                returnTo: `${window.location.origin}${AppRoutes.loggedOut}`,
                            },
                        });
                    }}
                    logoutThreshold={LOGOUT_THRESHOLD}
                />
            )}
            <Outlet />
        </>
    );
};
